import { Router } from '@angular/router';
import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InrShortPipe, NumberFormatPipe } from '../../../../../_pipes';
import { resolveCiPropertyType } from '../../ci-report';
import { AuthService } from '../../../../../_services/auth.service';

export type Amenities = Record<string, boolean>;

export type PriceItem = {
  _id: string;
  bhkType: string;
  price: number;
  areaInSq: number;
  averagePrice: number;
  propertyStructureType?: string;
};

@Component({
  selector: 'app-property-overview',
  standalone: true,
  imports: [
    CommonModule,
    InrShortPipe,
    NumberFormatPipe
  ],
  templateUrl: './property-overview.component.html',
  styleUrls: ['./property-overview.component.scss'],
})
export class PropertyOverviewComponent {
  readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  openIntelligencePlans(): void {
    this.router.navigate(['/payment'], { queryParams: { plan: 'intelligence' } });
  }

  @Input() selectedItem: any;
  @Input() propertyAccessGranted = false;

  get isLandProperty(): boolean {
    const type = resolveCiPropertyType(this.selectedItem?.ciPublicReport ?? this.selectedItem?.cipIntelligence ?? this.selectedItem?.ciRecord ?? {}, this.selectedItem ?? {});
    return type === 'Plots' || type === 'Independent House';
  }

  isDescriptionExpanded = false;
  showFullPrice = false;

  readonly DEFAULT_ICON = 'M12 4v16m8-8H4';

  readonly AMENITY_MAP: Record<
    string,
    { label: string; icon: string }
  > = {

    air_conditioned: {
      label: 'AC',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z'
    },

    wifi: {
      label: 'Wi-Fi',
      icon: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.343 9.257c5.858-5.858 15.355-5.858 21.213 0'
    },

    power_backup: {
      label: 'Power Backup',
      icon: 'M13 10V3L4 14h7v7l9-11h-7z'
    },

    vaastu: {
      label: 'Vaastu Compliant',
      icon: 'M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4z'
    },

    gymnasium: {
      label: 'Gym',
      icon: 'M6 10v4m0 0h2m-2 0H4m12-4v4m0 0h2m-2 0h-2M8 12h8'
    },

    swimming_pool: {
      label: 'Swimming Pool',
      icon: 'M2 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0M2 16c2-2 4-2 6 0s4 2 6 0 4-2 6 0'
    },

    security: {
      label: 'Security',
      icon: 'M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4z'
    },

    visitor_parking: {
      label: 'Visitor Parking',
      icon: 'M3 13l1-3h16l1 3M5 16h14M6 16v2m12-2v2M8 13V9h8v4'
    },

    maintenance_staff: {
      label: 'Maintenance',
      icon: 'M11 3a1 1 0 011 1v2.07a7.002 7.002 0 015.93 5.93H20a1 1 0 110 2h-2.07a7.002 7.002 0 01-5.93 5.93V20a1 1 0 11-2 0v-2.07a7.002 7.002 0 01-5.93-5.93H4a1 1 0 110-2h2.07A7.002 7.002 0 0111 6.07V4a1 1 0 011-1z'
    },

    security_24x7: {
      label: 'Security 24 x 7',
      icon: 'M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4zM9 12l2 2 4-4'
    },

    lift: {
      label: 'Lift',
      icon: 'M8 4h8M8 20h8M12 8v8M10 10l2-2 2 2M10 14l2 2 2-2'
    },

    private_terrace: {
      label: 'Private Terrace',
      icon: 'M4 20h16M6 20V10h12v10M3 10h18M8 6h8'
    },

    private_garage: {
      label: 'Private Garage',
      icon: 'M3 13l2-6h14l2 6M5 13v6m14-6v6M7 17h10M7 13h10'
    },

    private_jacuzzi: {
      label: 'Private Jacuzzi',
      icon: 'M4 14c2-2 4-2 6 0s4 2 6 0 4-2 6 0M4 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0'
    }
  };

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  togglePrice(): void {
    this.showFullPrice = !this.showFullPrice;
  }

  get propertyStructureTypes(): string[] {
    const types = new Map<string, string>();

    for (const price of this.getPrices()) {
      const type = price.propertyStructureType?.trim();
      if (type && !types.has(type.toLowerCase())) {
        types.set(type.toLowerCase(), type);
      }
    }

    return [...types.values()];
  }

  getPrices(): PriceItem[] {
    return this.selectedItem?.price || [];
  }

  formatAddress(item: any): string {
    if (!item) return '';

    return [
      item.name,
      item.address,
      item.streetName,
      item.cityName,
      item.stateName,
    ]
      .filter((part) => part && part.trim() !== '')
      .join(', ');
  }

  formatBuildingStr(input: any): string {
    if (!input || typeof input !== 'string') {
      return 'Invalid Input';
    }

    const clean = input
      .replace(/[,+]/g, ' ')
      .toUpperCase()
      .trim();

    const parts = clean.split(/\s+/);

    if (parts.length < 3) {
      return 'Invalid Format';
    }

    const [block, ground, floor] = parts;

    return `${block}, ${ground}+${floor}`;
  }

  getRoomIcon(room: string): string {
    const icons: Record<string, string> = {
      'Servant Room': 'fa-solid fa-user-tie',
      'Study Room': 'fa-solid fa-book-open',
      'Store Room': 'fa-solid fa-boxes-stacked',
      'Media Room': 'fa-solid fa-film',
      'Pooja Room': 'fa-solid fa-om',
      'Guest Room': 'fa-solid fa-bed',
    };

    return icons[room] || 'fa-solid fa-door-open';
  }

  hasAmenity(
    amenities: Amenities | null | undefined,
    key: keyof typeof this.AMENITY_MAP
  ): boolean {

    if (!amenities) {
      return false;
    }

    return amenities[key as string] === true;
  }

  getVisibleAmenities(amenitiesObj: any) {

    if (!amenitiesObj) {
      return [];
    }

    return Object.keys(amenitiesObj)
      .filter(
        (key) =>
          !!amenitiesObj[key] &&
          key !== 'undefined'
      )
      .map((key) => ({
        key,
        label:
          this.AMENITY_MAP[key]?.label ||
          this.formatKey(key),
        icon:
          this.AMENITY_MAP[key]?.icon ||
          this.DEFAULT_ICON,
      }));
  }

  formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
}
